import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Shield, FileText, Users, TrendingUp, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { USER_ROLE, PROVIDER_TYPES } from '@/utils/constants';

const AuthPage = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, text: '', color: '' });

  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
  });

  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: USER_ROLE.CLIENT,
    phone: '',
    provider_name: '',
    provider_type: '',
  });

  const checkPasswordStrength = (password) => {
    let score = 0;
    if (!password) return { score: 0, text: '', color: '' };

    // Length check
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;

    // Character variety checks
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 2) return { score, text: 'Weak', color: 'text-red-600 bg-red-100' };
    if (score <= 4) return { score, text: 'Medium', color: 'text-yellow-600 bg-yellow-100' };
    return { score, text: 'Strong', color: 'text-green-600 bg-green-100' };
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setRegisterData({ ...registerData, password: newPassword });
    setPasswordStrength(checkPasswordStrength(newPassword));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(registerData);
      toast.success('Registration successful! Please login.');
      setRegisterData({
        email: '',
        password: '',
        full_name: '',
        role: USER_ROLE.CLIENT,
        phone: '',
        provider_name: '',
        provider_type: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left side - Hero */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 text-white p-12 flex-col justify-between">
        <div>
          <div className="flex items-center gap-3 mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_broker-claims-1/artifacts/duvrk5eb_kind-insurance-logo.png" 
              alt="Kind Insurance Brokers" 
              className="h-16 w-auto"
            />
          </div>
          
          <h1 className="text-5xl font-bold mb-6 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Kind Insurance
            <br />
            Claims Portal
          </h1>
          
          <p className="text-lg text-slate-300 mb-12" style={{ fontFamily: 'Public Sans, sans-serif' }}>
            Streamline your insurance claims process with our comprehensive digital platform.
            Submit, track, and manage claims efficiently.
          </p>

          <div className="grid grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-md bg-blue-600 flex items-center justify-center flex-shrink-0">
                <FileText className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Digital FNOL</h3>
                <p className="text-sm text-slate-400" style={{ fontFamily: 'Public Sans, sans-serif' }}>Submit claims online instantly</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-md bg-blue-600 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Real-time Tracking</h3>
                <p className="text-sm text-slate-400" style={{ fontFamily: 'Public Sans, sans-serif' }}>Monitor claim progress live</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-md bg-blue-600 flex items-center justify-center flex-shrink-0">
                <Users className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Direct Communication</h3>
                <p className="text-sm text-slate-400" style={{ fontFamily: 'Public Sans, sans-serif' }}>Chat with brokers & adjusters</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-md bg-blue-600 flex items-center justify-center flex-shrink-0">
                <Shield className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Secure & Compliant</h3>
                <p className="text-sm text-slate-400" style={{ fontFamily: 'Public Sans, sans-serif' }}>Bank-level security standards</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-sm text-slate-400" style={{ fontFamily: 'Public Sans, sans-serif' }}>
          © 2026 Kind Insurance Brokers Limited. All rights reserved.
        </div>
      </div>

      {/* Right side - Auth Forms */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_broker-claims-1/artifacts/duvrk5eb_kind-insurance-logo.png" 
              alt="Kind Insurance Brokers" 
              className="h-12 w-auto"
            />
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Welcome Back</CardTitle>
                  <CardDescription style={{ fontFamily: 'Public Sans, sans-serif' }}>
                    Enter your credentials to access your account
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email" style={{ fontFamily: 'Public Sans, sans-serif' }}>Email</Label>
                      <Input
                        id="login-email"
                        data-testid="login-email-input"
                        type="email"
                        placeholder="you@example.com"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password" style={{ fontFamily: 'Public Sans, sans-serif' }}>Password</Label>
                      <div className="relative">
                        <Input
                          id="login-password"
                          data-testid="login-password-input"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      data-testid="login-submit-button"
                      className="w-full" 
                      disabled={loading}
                      style={{ fontFamily: 'Public Sans, sans-serif' }}
                    >
                      {loading ? 'Logging in...' : 'Login'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="register">
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Create Account</CardTitle>
                  <CardDescription style={{ fontFamily: 'Public Sans, sans-serif' }}>
                    Register to start submitting and managing claims
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="full-name" style={{ fontFamily: 'Public Sans, sans-serif' }}>Full Name</Label>
                      <Input
                        id="full-name"
                        data-testid="register-name-input"
                        placeholder="John Doe"
                        value={registerData.full_name}
                        onChange={(e) => setRegisterData({ ...registerData, full_name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" style={{ fontFamily: 'Public Sans, sans-serif' }}>Email</Label>
                      <Input
                        id="email"
                        data-testid="register-email-input"
                        type="email"
                        placeholder="you@example.com"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" style={{ fontFamily: 'Public Sans, sans-serif' }}>Phone (Optional)</Label>
                      <Input
                        id="phone"
                        data-testid="register-phone-input"
                        type="tel"
                        placeholder="+234 800 000 0000"
                        value={registerData.phone}
                        onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="provider-name" style={{ fontFamily: 'Public Sans, sans-serif' }}>Name of Service Provider *</Label>
                      <Input
                        id="provider-name"
                        data-testid="register-provider-name-input"
                        placeholder="Hospital/Clinic Name"
                        value={registerData.provider_name}
                        onChange={(e) => setRegisterData({ ...registerData, provider_name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="provider-type" style={{ fontFamily: 'Public Sans, sans-serif' }}>Provider Type *</Label>
                      <select
                        id="provider-type"
                        data-testid="register-provider-type-select"
                        className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                        value={registerData.provider_type}
                        onChange={(e) => setRegisterData({ ...registerData, provider_type: e.target.value })}
                        required
                      >
                        <option value="">Select provider type</option>
                        {PROVIDER_TYPES.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="role" style={{ fontFamily: 'Public Sans, sans-serif' }}>I am a *</Label>
                      <select
                        id="role"
                        data-testid="register-role-select"
                        className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                        value={registerData.role}
                        onChange={(e) => setRegisterData({ ...registerData, role: e.target.value })}
                        required
                      >
                        <option value={USER_ROLE.CLIENT}>Client / Service Provider</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password" style={{ fontFamily: 'Public Sans, sans-serif' }}>Password *</Label>
                      <div className="relative">
                        <Input
                          id="password"
                          data-testid="register-password-input"
                          type={showRegPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={registerData.password}
                          onChange={handlePasswordChange}
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowRegPassword(!showRegPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                        >
                          {showRegPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {registerData.password && (
                        <div className="flex items-center gap-2 mt-1">
                          <div className="text-xs font-medium px-2 py-1 rounded ${passwordStrength.color}">
                            {passwordStrength.text}
                          </div>
                          <div className="text-xs text-slate-500">
                            {passwordStrength.score < 4 && 'Use 8+ characters with mix of letters, numbers & symbols'}
                          </div>
                        </div>
                      )}
                    </div>
                    <Button 
                      type="submit" 
                      data-testid="register-submit-button"
                      className="w-full" 
                      disabled={loading}
                      style={{ fontFamily: 'Public Sans, sans-serif' }}
                    >
                      {loading ? 'Creating account...' : 'Create Account'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
